import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import { useSession } from 'next-auth/react'
import { useUser } from './UserProvider'

interface UnifiedAuthData {
  profilePhoto: string | null
  displayName: string
  firstName: string
  lastName: string
  email: string | null
  isAuthenticated: boolean
  authSource: 'nextauth' | 'none'
}

interface UnifiedAuthContextType {
  authData: UnifiedAuthData
  refreshAuth: () => void
}

const UnifiedAuthContext = createContext<UnifiedAuthContextType | undefined>(undefined)

export const useUnifiedAuth = () => {
  const context = useContext(UnifiedAuthContext)
  if (!context) {
    throw new Error('useUnifiedAuth must be used within a UnifiedAuthProvider')
  }
  return context
}

interface UnifiedAuthProviderProps {
  children: ReactNode
}

// Helper function to check if a photo URL is a default photo
const isDefaultPhoto = (url: string): boolean => url.includes('default')

export const UnifiedAuthProvider: React.FC<UnifiedAuthProviderProps> = ({ children }) => {
  const { data: session, status } = useSession()
  const { profile, loggedIn } = useUser()

  // Initialize with default state
  const [authData, setAuthData] = useState<UnifiedAuthData>({
    profilePhoto: null,
    displayName: '',
    firstName: '',
    lastName: '',
    email: null,
    isAuthenticated: false,
    authSource: 'none'
  })

  const refreshAuth = useCallback(() => {
    // Only use NextAuth session
    if (status === 'authenticated' && session?.user) {
      const nameParts = session.user.name?.split(' ') || []

      // Get photo from UserProvider profile if available (it syncs with NextAuth)
      let profilePhoto = null
      if (loggedIn && profile && profile.photos?.url && !isDefaultPhoto(profile.photos.url)) {
        profilePhoto = profile.photos.url
      }

      setAuthData({
        profilePhoto,
        displayName: session.user.name || '',
        firstName: nameParts[0] || '',
        lastName: nameParts.slice(1).join(' ') || '',
        email: session.user.email,
        isAuthenticated: true,
        authSource: 'nextauth'
      })
    } else {
      // Not authenticated
      setAuthData({
        profilePhoto: null,
        displayName: '',
        firstName: '',
        lastName: '',
        email: null,
        isAuthenticated: false,
        authSource: 'none'
      })
    }
  }, [session, profile, loggedIn, status])

  // Update auth data when session or profile changes
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- synchronizes derived auth state from the external NextAuth session/UserProvider; refreshAuth is also exposed on context for manual refresh, so this must stay an effect-driven sync
    refreshAuth()
  }, [refreshAuth])

  return (
    <UnifiedAuthContext.Provider value={{ authData, refreshAuth }}>
      {children}
    </UnifiedAuthContext.Provider>
  )
}